/**
 * Derivación al Órgano de Control Interno. Lo comparten las vistas 7 y 10.
 *
 * `NEG-13`: son **dos causas de naturaleza opuesta**, y el OCI necesita saber
 * cuál es. `documento_falso` es un hallazgo sustantivo —una entidad negó la
 * autenticidad—; `no_acreditado` es un fallo administrativo —nadie contestó
 * tras agotar los requerimientos—. El prototipo las confundía en un solo rojo
 * (`P-1`), y por eso aquí el botón de confirmación **nombra la causa** en vez
 * de decir «Derivar».
 *
 * Es irreversible: genera un informe numerado.
 */
import type { CausaDerivacion } from '../api/tipos.js'
import { Callout } from '@mpfn/patterns'
import { useDerivarAlOci } from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { DialogoConMotivo } from './DialogoConMotivo.js'

interface Props {
  documentoId: string
  denominacion: string
  causa: CausaDerivacion
  abierto: boolean
  onCambio: (abierto: boolean) => void
}

export function DialogoDerivacion({ documentoId, denominacion, causa, abierto, onCambio }: Props) {
  const catalogos = useCatalogos()
  const derivar = useDerivarAlOci()
  const etiquetaCausa = etiquetaDe(catalogos.causasDerivacion, causa)

  return (
    <DialogoConMotivo
      abierto={abierto}
      onCambio={onCambio}
      titulo="Derivar al Órgano de Control Interno"
      descripcion={`${denominacion} · causa: ${etiquetaCausa.toLowerCase()}.`}
      etiquetaConfirmar={`Derivar por ${etiquetaCausa.toLowerCase()}`}
      cargando={derivar.isPending}
      advertencia={
        <Callout
          tone={causa === 'documento_falso' ? 'critical' : 'warning'}
          title="La derivación es irreversible"
        >
          {causa === 'documento_falso'
            ? 'Una entidad negó la autenticidad del documento. Se generará un informe numerado dirigido al OCI.'
            : 'Se agotaron los requerimientos sin obtener respuesta. Se generará un informe numerado dirigido al OCI.'}
        </Callout>
      }
      onConfirmar={(motivo) => {
        derivar.mutate(
          { documentoId, causa, motivo },
          {
            onSuccess: (datos) => {
              avisarExito('Documento derivado al OCI', `Informe ${datos.numeroInforme}.`)
              onCambio(false)
            },
            onError: (error) => {
              avisarError(error, 'No se pudo derivar al OCI')
            },
          },
        )
      }}
    />
  )
}
